import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { Course, CourseService } from '../../services/course.service';
import { CreatorFocusService } from '../../services/creator-focus.service';
import { CreatorLibraryService } from '../../services/creator-library.service';

@Component({
  selector: 'app-courses',
  templateUrl: './courses.component.html',
  styleUrl: './courses.component.scss',
})
export class CoursesComponent implements OnInit, OnDestroy {
  @ViewChild('myIframe', { static: false }) myIframe: ElementRef;

  iframeUrl = '';
  iframeLoading = true;
  private iframeLoadingStartedAt = 0;
  private iframeLoadFallbackTimeout?: ReturnType<typeof setTimeout>;
  private iframeReadyReceived = false;
  private readonly minIframeLoadingMs = 700;
  private readonly iframeLoadFallbackMs = 2000;
  private authTokenSubscription?: Subscription;
  private readonly canvasOrigin = new URL(environment.courseGenerationUrl).origin;
  private readonly canvasReadyMessageType = 'ALPHABEES_CANVAS_READY';
  // Canvas asks for a fresh token when it hits a 401. It cannot mint one
  // itself — the refresh_token lives here, not in the iframe.
  private readonly canvasTokenRequestMessageType = 'REQUEST_AUTH_TOKEN';
  // The course editor opened straight from the portal (`?item=…&kind=…`) was
  // closed: back to the library it came from.
  private readonly canvasDoneMessageType = 'ALPHABEES_CREATOR_DONE';
  /** Opened for one task (an item, a quick start): the X leads back to the
   *  Creator start. Without one this is the canvas start page itself (tenants
   *  without the library), and an X would lead straight back here. */
  hasTask = false;
  /** Asked once: does this tenant have the library (and so the new start)? */
  private libraryChecked = false;

  constructor(
    protected courseService: CourseService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected authService: AuthService,
    readonly creatorFocus: CreatorFocusService,
    private zone: NgZone,
    private library: CreatorLibraryService
  ) {
    this.authTokenSubscription = this.authService.accessToken$.subscribe(() => {
      this.sendMessageToIframe();
    });
  }

  get courses() {
    return this.courseService.courses$.value;
  }

  sendMessageToIframe() {
    const message = {
      type: 'AUTH_TOKEN',
      token: this.authService.accessToken$.value,
    };

    // Only to the creator's own origin. With '*' the token went to whatever
    // page the iframe happened to show (Phase 0, Sofort-PR 3).
    if (this.myIframe && this.myIframe.nativeElement.contentWindow) {
      this.myIframe.nativeElement.contentWindow.postMessage(message, this.canvasOrigin);
    }
  }

  ngOnInit(): void {
    window.addEventListener('message', this.handleIframeMessage);
    // No token in the URL: it ended up in server logs, browser history and
    // Referer headers. The creator asks for it by postMessage and waits for
    // AUTH_TOKEN before it starts (canvas UserProvider).
    // Forward exercise/course query params to the canvas iframe;
    // `item` = library content to open ("Im Creator bearbeiten", Phase 2 PR 3)
    const forwardParams = [
      'mode', 'title', 'description', 'exerciseType', 'exerciseTypes',
      'difficultyLevel', 'learningGoals', 'scope', 'bucketIds', 'item', 'kind',
      // a comment thread to open with the course (link in a comment mail)
      'thread',
    ];
    this.route.queryParams.subscribe((qp) => {
      // fresh per navigation: the component stays when only the query
      // changes, and an old `item` must not ride along into `mode=exercise`
      const params = new URLSearchParams();
      for (const key of forwardParams) {
        if (qp[key]) {
          params.set(key, qp[key]);
        }
      }
      const query = params.toString();
      this.hasTask = !!query;
      // Without a task this is the old canvas start page. Tenants with the
      // library have the new start at `/creator` — an old bookmark or a typed
      // `/creator/workspace` goes there (Marcel, 29.10.2026). Without the
      // library (`no`) or on an error the canvas start stays, as before; the
      // start page sends those tenants here, so no loop.
      if (!query && !this.libraryChecked) {
        this.libraryChecked = true;
        this.library.available().subscribe((answer) => {
          if (answer === 'yes') {
            this.router.navigate(['/creator'], { replaceUrl: true });
            return;
          }
          this.startIframeLoading();
          this.iframeUrl = environment.courseGenerationUrl;
        });
        return;
      }
      const nextIframeUrl = query
        ? `${environment.courseGenerationUrl}?${query}`
        : environment.courseGenerationUrl;
      if (nextIframeUrl !== this.iframeUrl) {
        this.startIframeLoading();
        this.iframeUrl = nextIframeUrl;
      }
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.handleIframeMessage);
    // leaving the page never leaves the portal without its navigation
    this.creatorFocus.end();
    this.clearIframeLoadFallback();
    this.authTokenSubscription?.unsubscribe();
  }

  onIframeLoaded() {
    // every load of the creator (first, new query, its own reload) starts in
    // the normal layout — a page that died in focus must not keep the portal there
    this.creatorFocus.end();
    this.sendMessageToIframe();
    if (!this.iframeReadyReceived) {
      this.scheduleIframeLoadFallback();
    }
  }

  private handleIframeMessage = (event: MessageEvent) => {
    if (event.origin !== this.canvasOrigin) {
      return;
    }

    const iframeWindow = this.myIframe?.nativeElement?.contentWindow;
    if (iframeWindow && event.source !== iframeWindow) {
      return;
    }

    if (event.data?.type === this.canvasReadyMessageType) {
      this.zone.run(() => {
        this.iframeReadyReceived = true;
        this.finishIframeLoading();
      });
    }

    if (event.data?.type === this.canvasTokenRequestMessageType) {
      this.zone.run(() => this.sendFreshTokenToIframe());
    }

    if (event.data?.type === this.canvasDoneMessageType) {
      this.zone.run(() => this.router.navigate(['/creator/library']));
    }

    if (event.data?.type === 'ALPHABEES_LAYOUT') {
      this.zone.run(() => this.creatorFocus.apply(event.data));
    }
  };

  /** The way back: the portal shows its navigation, whatever canvas thinks. */
  endFocus() {
    this.creatorFocus.pause();
  }

  /** And forward again, while the editor is still open. */
  resumeFocus() {
    this.creatorFocus.resume();
  }

  /**
   * Renew the access token and hand it to the canvas iframe.
   *
   * The 5-minute refresh timer does not run while the machine sleeps or the
   * tab is throttled, so canvas can hit an expired token between ticks. On
   * failure we still push the current token — that is strictly better than
   * leaving the iframe with nothing, and canvas surfaces the original error.
   */
  private sendFreshTokenToIframe() {
    this.authService.refreshToken().subscribe({
      next: () => this.sendMessageToIframe(),
      error: () => this.sendMessageToIframe(),
    });
  }

  private startIframeLoading() {
    this.clearIframeLoadFallback();
    this.iframeLoadingStartedAt = Date.now();
    this.iframeReadyReceived = false;
    this.iframeLoading = true;
  }

  private finishIframeLoading() {
    if (!this.iframeLoading) {
      return;
    }

    const elapsed = Date.now() - this.iframeLoadingStartedAt;
    const delay = Math.max(0, this.minIframeLoadingMs - elapsed);

    this.clearIframeLoadFallback();
    this.iframeLoadFallbackTimeout = setTimeout(() => {
      this.iframeLoading = false;
      this.sendMessageToIframe();
    }, delay);
  }

  private scheduleIframeLoadFallback() {
    this.clearIframeLoadFallback();
    this.iframeLoadFallbackTimeout = setTimeout(() => {
      this.finishIframeLoading();
    }, this.iframeLoadFallbackMs);
  }

  private clearIframeLoadFallback() {
    if (this.iframeLoadFallbackTimeout) {
      clearTimeout(this.iframeLoadFallbackTimeout);
      this.iframeLoadFallbackTimeout = undefined;
    }
  }

  getCoverImage(course: Course) {
    return `url(${course.coverImage.media.image.src})`;
  }

  openCourse(id: string) {
    this.router.navigate(['/courses', id]);
  }
}
